import { toRefs, watch } from 'vue';
import { useCommonChartOptions, useLastValue } from '../useChart';
import { scpmUtil } from '@/utils/scpmUtil.js';

const fontFamilySKR = "'Noto Sans KR', Arial, sans-serif";

const { getColorOfChart } = scpmUtil;
const useState = (state) => state.value;

const getChartOptionsDefault = (displayUnit, getPositionTooltip, enableLegend) => {
  return {
    grid: [
      {
        bottom: 30,
        containLabel: true,
        left: 16,
        right: 16,
        top: 30
      }
    ],
    legend: {
      bottom: 0,
      icon: 'circle',
      itemGap: 20,
      itemHeight: 8,
      itemWidth: 8,
      left: 16,
      textStyle: {
        color: '#555B68',
        fontSize: 12,
        fontFamily: fontFamilySKR
      },
      type: 'scroll',
      ...(!enableLegend && {
        left: '30px',
        bottom: 0,
        icon: 'circle',
        type: 'scroll',
        padding: 8,
        data: []
      })
    },
    series: [],
    xAxis: [
      {
        gridIndex: 0,
        type: 'time',
        min: null,
        max: null,
        nameLocation: 'center',
        splitNumber: 3,
        boundaryGap: true,
        silent: false,
        nameTextStyle: {
          color: '#8e9198',
          fontSize: 12,
          fontFamily: fontFamilySKR
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#787c84'
          }
        },
        axisLabel: {
          interval: 0,
          color: '#8e9198',
          fontSize: 10,
          fontFamily: fontFamilySKR,
          formatter: null
        },
        splitLine: { show: false },
        axisTick: { show: false }
      }
    ],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        name: displayUnit,
        axisLabel: {
          interval: 0,
          color: '#8e9198',
          fontSize: 12,
          fontFamily: fontFamilySKR,
          formatter: null
        },
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: {
            color: '#787c84'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#dddee0'
          }
        },
        splitNumber: 2,
        axisTick: { show: false },
        show: true,
        min: null,
        max: null
      }
    ],
    tooltip: {
      backgroundColor: '#fff',
      textStyle: {
        color: '#000',
        fontSize: 12
      },
      trigger: 'axis',
      axisPointer: {
        type: 'line'
      },
      confine: false,
      renderMode: 'html',
      className: 'toolbar-scrollbar',
      extraCssText:
        'max-height: 300px; overflow:auto; max-width: 600px; padding: 12px;display: flex;flex-direction: column;gap: 8px;',
      triggerOn: 'mousemove',
      enterable: true,
      transitionDuration: 0.8,
      appendToBody: true,
      valueFormatter: null,
      position: (point, params, dom, rect, size) => {
        const position = getPositionTooltip(point, size);
        let top = position[1];
        let left = position[0];

        if (size.contentSize[0] > 800) {
          left = position[0] / 4;
        }

        if (size.contentSize[1] > 80) {
          top = point[1] + 10;
        }

        return [left, top];
      }
    },
    axisPointer: {
      snap: true,
      lineStyle: { type: 'dashed' },
      label: { show: true, margin: 6, backgroundColor: '#556', color: '#fff' },
      link: [{ xAxisId: ['chart1', 'chart2'] }, { xAxisId: ['chart2', 'chart1'] }]
    },
    animation: true,
    animationThreshold: 2000,
    animationEasingUpdate: 'cubicOut',
    animationDurationUpdate: 1500,
    progressive: 500,
    progressiveThreshold: 2000
  };
};

export const useChartOptions = (
  chartSeries,
  chartType,
  { lineColor, getPositionTooltip, metricUnit, displayUnit, enableLegend },
  props
) => {
  const { lastSeries, lastValueArr, updateLastValue } = useLastValue();
  const { showTooltipOfChart } = toRefs(props);
  const {
    chartOptions,
    setSeries,
    setYAxisMinMax,
    setXAxisFormatter,
    setXAxisMinMax,
    getYminMax,
    convertUnit,
    setShowTooltip,
    unitConfig
  } = useCommonChartOptions(
    getChartOptionsDefault(displayUnit, getPositionTooltip, enableLegend),
    chartType,
    metricUnit,
    displayUnit
  );

  // set

  watch(
    [() => chartSeries, () => chartType],
    () => {
      const displayData = useState(chartSeries);
      let lstColor = getColorOfChart(displayData.length);
      const seriesData = displayData.map((el, idx) => {
        const { data } = el;
        return {
          ...el,
          data: data,
          silent: true,
          cursor: 'default',
          symbol: 'circle',
          lineStyle: { width: 1 },
          itemStyle: { color: lineColor[idx] ? lineColor[idx] : lstColor[idx] },
          symbolSize: 3
        };
      });
      const { max: refValue } = getYminMax(displayData, useState(chartType));
      const { data } = convertUnit(seriesData, refValue, metricUnit);
      setYAxisMinMax(displayData);
      updateLastValue(data);
      setSeries(lastSeries.value);
      setShowTooltip(showTooltipOfChart.value);
    },
    { deep: true }
  );

  watch(
    () => showTooltipOfChart.value,
    (value) => setShowTooltip(value)
  );
  return { chartOptions, lastValueArr, setXAxisMinMax, setXAxisFormatter, unitConfig };
};
